/**
 * Buckets / categories page (FE-3, PHASE1 §4 bucket-groups + buckets, §7). Shows
 * the group→bucket tree with create/edit/archive/reorder for both levels. The
 * System group and Transfer bucket are protected from archive/move.
 */
import { useMemo, useState } from 'react';
import { Plus, Tags } from 'lucide-react';
import { PageHeader } from '../components/layout/PageHeader.tsx';
import { Button } from '../components/ui/Button.tsx';
import { EmptyState } from '../components/ui/EmptyState.tsx';
import { ErrorState } from '../components/ui/ErrorState.tsx';
import { Skeleton } from '../components/ui/Skeleton.tsx';
import { ConfirmDialog } from '../components/ui/ConfirmDialog.tsx';
import { useBucketTree, useBucketTreeQuery } from '../lib/hooks/useBuckets.ts';
import {
  useReorderBucketGroups,
  useReorderBuckets,
  useUpdateBucket,
  useUpdateBucketGroup,
} from '../lib/hooks/useBucketMutations.ts';
import type { BucketGroupOut, BucketGroupWithBuckets, BucketOut } from '../lib/types.ts';
import { BucketGroupSection } from './buckets/BucketGroupSection.tsx';
import { BucketFormDialog } from './buckets/BucketFormDialog.tsx';
import { BucketGroupFormDialog } from './buckets/BucketGroupFormDialog.tsx';

const SYSTEM_GROUP_SLUG = 'system';

export default function BucketsPage() {
  const query = useBucketTreeQuery();
  const [includeArchived, setIncludeArchived] = useState(false);
  const tree = useBucketTree(includeArchived);

  const reorderGroups = useReorderBucketGroups();
  const reorderBuckets = useReorderBuckets();
  const updateGroup = useUpdateBucketGroup();
  const updateBucket = useUpdateBucket();

  const [groupFormOpen, setGroupFormOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<BucketGroupOut | null>(null);
  const [bucketFormOpen, setBucketFormOpen] = useState(false);
  const [editingBucket, setEditingBucket] = useState<BucketOut | null>(null);
  const [bucketGroupId, setBucketGroupId] = useState<number | null>(null);
  const [archiveGroupTarget, setArchiveGroupTarget] = useState<BucketGroupWithBuckets | null>(null);

  /** Groups that participate in reordering (everything but the System group). */
  const reorderable = useMemo(
    () => tree.filter((g) => g.slug !== SYSTEM_GROUP_SLUG),
    [tree],
  );

  const reordering = reorderGroups.isPending || reorderBuckets.isPending;

  function moveGroup(group: BucketGroupWithBuckets, dir: -1 | 1) {
    const index = reorderable.findIndex((g) => g.id === group.id);
    if (index === -1) return;
    const next = [...reorderable];
    const target = index + dir;
    if (target < 0 || target >= next.length) return;
    const [moved] = next.splice(index, 1);
    next.splice(target, 0, moved);
    reorderGroups.mutate(next.map((g) => g.id));
  }

  function openCreateGroup() {
    setEditingGroup(null);
    setGroupFormOpen(true);
  }

  function openEditGroup(group: BucketGroupOut) {
    setEditingGroup(group);
    setGroupFormOpen(true);
  }

  function openCreateBucket(groupId: number) {
    setEditingBucket(null);
    setBucketGroupId(groupId);
    setBucketFormOpen(true);
  }

  function openEditBucket(bucket: BucketOut) {
    setEditingBucket(bucket);
    setBucketGroupId(bucket.group_id);
    setBucketFormOpen(true);
  }

  function archiveBucket(bucket: BucketOut) {
    updateBucket.mutate({ id: bucket.id, body: { archived: !bucket.archived } });
  }

  function confirmArchiveGroup() {
    if (!archiveGroupTarget) return;
    updateGroup.mutate(
      { id: archiveGroupTarget.id, body: { archived: !archiveGroupTarget.archived } },
      { onSettled: () => setArchiveGroupTarget(null) },
    );
  }

  return (
    <div>
      <PageHeader
        title="Kategorien"
        actions={
          <Button size="sm" onClick={openCreateGroup}>
            <Plus className="h-4 w-4" />
            <span className="hidden sm:inline">Neue Gruppe</span>
          </Button>
        }
      />

      <div className="w-full space-y-8 px-4 py-6 sm:px-6">
        {query.isPending ? (
          <div className="space-y-4">
            <Skeleton className="h-32 w-full rounded-lg" />
            <Skeleton className="h-32 w-full rounded-lg" />
          </div>
        ) : query.isError ? (
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        ) : tree.length === 0 ? (
          <EmptyState
            icon={Tags}
            title="Noch keine Kategorien"
            description="Lege eine Gruppe an, um deine Ausgaben zu strukturieren."
            action={
              <Button size="sm" onClick={openCreateGroup}>
                <Plus className="h-4 w-4" />
                Neue Gruppe
              </Button>
            }
          />
        ) : (
          <>
            <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
              <input
                type="checkbox"
                checked={includeArchived}
                onChange={(e) => setIncludeArchived(e.target.checked)}
                className="h-4 w-4 rounded border border-slate-200 accent-sky-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-600 dark:border-slate-700 dark:accent-sky-500 dark:focus-visible:outline-sky-500"
              />
              Archivierte anzeigen
            </label>

            {tree.map((group) => {
              const reorderIndex = reorderable.findIndex((g) => g.id === group.id);
              return (
                <BucketGroupSection
                  key={group.id}
                  group={group}
                  isFirst={reorderIndex <= 0}
                  isLast={reorderIndex === reorderable.length - 1}
                  reordering={reordering}
                  onMoveGroup={(dir) => moveGroup(group, dir)}
                  onEditGroup={() => openEditGroup(group)}
                  onArchiveGroup={() => setArchiveGroupTarget(group)}
                  onAddBucket={() => openCreateBucket(group.id)}
                  onEditBucket={openEditBucket}
                  onArchiveBucket={archiveBucket}
                  onReorderBuckets={(orderedIds) =>
                    reorderBuckets.mutate({ groupId: group.id, orderedIds })
                  }
                />
              );
            })}
          </>
        )}
      </div>

      <BucketGroupFormDialog
        open={groupFormOpen}
        onOpenChange={setGroupFormOpen}
        group={editingGroup}
      />
      <BucketFormDialog
        open={bucketFormOpen}
        onOpenChange={setBucketFormOpen}
        bucket={editingBucket}
        defaultGroupId={bucketGroupId}
      />
      <ConfirmDialog
        open={archiveGroupTarget !== null}
        onOpenChange={(o) => {
          if (!o) setArchiveGroupTarget(null);
        }}
        title={archiveGroupTarget?.archived ? 'Gruppe reaktivieren' : 'Gruppe archivieren'}
        message={
          archiveGroupTarget?.archived
            ? `„${archiveGroupTarget?.name}" wieder aktivieren?`
            : `„${archiveGroupTarget?.name}" archivieren? Die Gruppe und ihre Kategorien werden ausgeblendet.`
        }
        confirmLabel={archiveGroupTarget?.archived ? 'Reaktivieren' : 'Archivieren'}
        danger={!archiveGroupTarget?.archived}
        loading={updateGroup.isPending}
        onConfirm={confirmArchiveGroup}
      />
    </div>
  );
}
